import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * Hook to fetch and manage collection schema
 * @param {string} collectionName - Name of the collection (e.g., 'vehicles', 'tools')
 * @returns {object} - { schema, fields, loading, error, refetch }
 */
export const useCollectionSchema = (collectionName) => {
    const [schema, setSchema] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSchema = async () => {
        try {
            setLoading(true);
            // Use public schemas endpoint (authenticated but not admin-only)
            const response = await axios.get(`${API}/schemas`);
            const collections = response.data;
            const found = collections.find(c => c.name === collectionName);
            
            if (found) {
                setSchema(found);
            } else {
                setError(`Collection "${collectionName}" not found`);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (collectionName) {
            fetchSchema();
        }
    }, [collectionName]);

    return {
        schema,
        fields: schema?.fields || [],
        displayName: schema?.display_name || collectionName,
        loading,
        error,
        refetch: fetchSchema
    };
};

/**
 * Hook to fetch reference data for select fields
 * @param {Array} fields - Array of field definitions
 * @returns {object} - { references, loading }
 */
export const useReferenceData = (fields) => {
    const [references, setReferences] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchReferences = async () => {
            const refFields = fields.filter(f => (f.type === "reference" || f.type === "multi_reference") && f.ref_collection);
            if (refFields.length === 0) return;

            setLoading(true);
            const refs = {};

            // Known system collections with their endpoints
            const systemCollections = {
                "vehicles": "vehicles",
                "projects": "projects",
                "tasks": "tasks",
                "tools": "tools",
                "daily_logs": "daily-logs",
                "users": "users/list"
            };

            for (const field of refFields) {
                const collectionName = field.ref_collection;
                try {
                    let response;
                    if (systemCollections[collectionName]) {
                        // Use direct endpoint for system collections
                        response = await axios.get(`${API}/${systemCollections[collectionName]}`);
                    } else {
                        // Use collections endpoint for custom collections
                        response = await axios.get(`${API}/collections/${collectionName}`);
                    }
                    refs[collectionName] = response.data;
                } catch (err) {
                    console.error(`Error fetching reference ${collectionName}:`, err);
                    // Try fallback to collections endpoint
                    try {
                        const fallback = await axios.get(`${API}/collections/${collectionName}`);
                        refs[collectionName] = fallback.data;
                    } catch (e) {
                        refs[collectionName] = [];
                    }
                }
            }

            setReferences(refs);
            setLoading(false);
        };

        if (fields.length > 0) {
            fetchReferences();
        }
    }, [fields]);

    return { references, loading };
};

export default useCollectionSchema;
